import { code } from "@streamdown/code";
import { mermaid as mermaidPlugin } from "@streamdown/mermaid";
import { Download, Eye, History, ImageOff } from "lucide-react";
import mermaid from "mermaid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BundledTheme } from "shiki";
import { Streamdown } from "streamdown";
import { extractAttachmentUuid, isPreviewableFile, openDocumentPreview } from "@/components/chat/DocumentPreviewModal";
import {
  getAttachmentFilename,
  getAttachmentVersionInfo,
  openDocumentVersionPanel,
} from "@/components/chat/documentVersionEvents";
import { getApiBaseUrl } from "@/config/runtime";
import { getHeaders } from "@/lib/api";

import "streamdown/styles.css";

// Import highlight.js dark theme as fallback for any non-Shiki paths
import "highlight.js/styles/github-dark.css";

// Mermaid theme configuration keyed by resolved theme
const MERMAID_THEMES = {
  dark: {
    theme: "dark" as const,
    themeVariables: {
      primaryColor: "#7C16F5",
      primaryTextColor: "#e2e8f0",
      primaryBorderColor: "#475569",
      lineColor: "#94a3b8",
      secondaryColor: "#EC622B",
      tertiaryColor: "#1e293b",
      background: "#0f172a",
      mainBkg: "#1e293b",
      nodeBorder: "#475569",
      clusterBkg: "#1e293b",
      clusterBorder: "#475569",
      titleColor: "#e2e8f0",
      edgeLabelBackground: "#1e293b",
      nodeTextColor: "#e2e8f0",
    },
  },
  light: {
    theme: "default" as const,
    themeVariables: {
      primaryColor: "#7C16F5",
      primaryTextColor: "#1e293b",
      primaryBorderColor: "#cbd5e1",
      lineColor: "#64748b",
      secondaryColor: "#EC622B",
      tertiaryColor: "#f1f5f9",
      background: "#ffffff",
      mainBkg: "#f8fafc",
      nodeBorder: "#cbd5e1",
      clusterBkg: "#f1f5f9",
      clusterBorder: "#cbd5e1",
      titleColor: "#0f172a",
      edgeLabelBackground: "#ffffff",
      nodeTextColor: "#0f172a",
    },
  },
};

function initMermaid() {
  const isDark = document.documentElement.classList.contains("dark");
  const config = isDark ? MERMAID_THEMES.dark : MERMAID_THEMES.light;
  mermaid.initialize({
    startOnLoad: false,
    ...config,
    fontFamily: "ui-sans-serif, system-ui, sans-serif",
    flowchart: { useMaxWidth: true, htmlLabels: true },
    sequence: { useMaxWidth: true },
  });
}

initMermaid();

let mermaidIdCounter = 0;

function MermaidDiagram({ code: codeStr }: Readonly<{ code: string }>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(`mermaid-${++mermaidIdCounter}`);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      try {
        const { svg: rendered } = await mermaid.render(idRef.current, codeStr.trim());
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to render diagram");
          setSvg("");
        }
        // Clean up any error element mermaid may have injected
        const errorEl = document.getElementById(`d${idRef.current}`);
        if (errorEl) errorEl.remove();
      }
    }

    render();
    return () => {
      cancelled = true;
    };
  }, [codeStr]);

  const handleDownload = useCallback(
    (format: "svg" | "png") => {
      if (!svg) return;

      if (format === "svg") {
        const blob = new Blob([svg], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "diagram.svg";
        a.click();
        URL.revokeObjectURL(url);
        return;
      }

      // PNG export via canvas
      const svgEl = containerRef.current?.querySelector("svg");
      if (!svgEl) return;

      const canvas = document.createElement("canvas");
      const bbox = svgEl.getBoundingClientRect();
      const scale = 2; // retina
      canvas.width = bbox.width * scale;
      canvas.height = bbox.height * scale;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(scale, scale);

      const img = new Image();
      const svgBlob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        const a = document.createElement("a");
        a.download = "diagram.png";
        a.href = canvas.toDataURL("image/png");
        a.click();
      };
      img.src = url;
    },
    [svg],
  );

  if (error) {
    return (
      <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 my-2">
        <p className="text-red-400 text-sm mb-2">Failed to render Mermaid diagram</p>
        <code className="block bg-surface-primary p-3 rounded-sm text-sm text-content-tertiary overflow-x-auto whitespace-pre">
          {codeStr}
        </code>
      </div>
    );
  }

  return (
    <div className="my-4 relative group">
      <div
        ref={containerRef}
        className="bg-surface-primary rounded-lg p-4 overflow-x-auto [&_svg]:max-w-full"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {svg && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => handleDownload("svg")}
            className="flex items-center gap-1 bg-surface-tertiary hover:bg-interactive-active text-content-secondary text-xs px-2 py-1 rounded-sm transition"
            title="Download SVG"
          >
            <Download className="w-3 h-3" />
            SVG
          </button>
          <button
            onClick={() => handleDownload("png")}
            className="flex items-center gap-1 bg-surface-tertiary hover:bg-interactive-active text-content-secondary text-xs px-2 py-1 rounded-sm transition"
            title="Download PNG"
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Authenticated image component — fetches internal API images with auth
// headers and creates object URLs for display.  External URLs (SAS, https)
// are passed through directly.
// ============================================================================

/**
 * Detects whether a URL is a relative API path that needs authenticated
 * fetching (e.g. `/chat/attachments/{uuid}/file`).
 */
export function isInternalApiPath(src: string): boolean {
  return src.startsWith("/chat/") || src.startsWith("/api/");
}

/**
 * Parse the filename out of a Content-Disposition header, supporting both the
 * RFC 5987 `filename*=UTF-8''...` form and the plain `filename="..."` form.
 * Returns undefined when no filename is present.
 */
export function filenameFromContentDisposition(header: string | null): string | undefined {
  if (!header) return undefined;
  const star = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain?.[1]?.trim() || undefined;
}

function AuthenticatedImage(props: React.ImgHTMLAttributes<HTMLImageElement>) {
  const { src, alt, ...rest } = props;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!src || !isInternalApiPath(src)) return;

    let cancelled = false;
    setLoading(true);
    setError(false);

    (async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const fullUrl = `${baseUrl}${src}`;
        const headers = await getHeaders();
        // Remove Content-Type for a GET request
        headers.delete("Content-Type");

        const response = await fetch(fullUrl, { headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const blob = await response.blob();
        if (cancelled) return;

        const url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch authenticated image:", err);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Clean up object URL on unmount
      setObjectUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [src]);

  // External URL — render directly
  if (!src || !isInternalApiPath(src)) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" className="inline-block my-2">
        <img
          src={src}
          alt={alt}
          className="rounded-lg border border-border-secondary max-w-full max-h-[400px] object-contain hover:opacity-90 transition-opacity"
          loading="lazy"
          {...rest}
        />
      </a>
    );
  }

  if (loading) {
    return (
      <span className="inline-flex items-center gap-2 my-2 px-4 py-3 rounded-lg bg-surface-secondary text-content-tertiary text-sm">
        <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />{" "}
        Loading image...
      </span>
    );
  }

  if (error || !objectUrl) {
    return (
      <span className="inline-flex items-center gap-2 my-2 px-4 py-3 rounded-lg bg-surface-secondary text-content-tertiary text-sm">
        <ImageOff className="w-4 h-4" />
        {alt || "Image could not be loaded"}
      </span>
    );
  }

  return (
    <a href={objectUrl} target="_blank" rel="noopener noreferrer" className="inline-block my-2">
      <img
        src={objectUrl}
        alt={alt}
        className="rounded-lg border border-border-secondary max-w-full max-h-[400px] object-contain hover:opacity-90 transition-opacity"
        {...rest}
      />
    </a>
  );
}

// ============================================================================
// Stable references — these MUST be defined at module level (outside the
// component) so they keep the same object identity across renders.  Streamdown
// uses a custom React.memo comparator that checks `plugins === prevPlugins`
// and caches its unified processor in a WeakMap keyed on the plugins object.
// If we created these inline inside the component, every render would produce
// a new object, defeating memoisation and causing the processor cache to miss,
// which manifests as raw markdown text instead of rendered HTML.
// ============================================================================

const STREAMDOWN_PLUGINS = { code, mermaid: mermaidPlugin };

const STREAMDOWN_SHIKI_THEME: [BundledTheme, BundledTheme] = ["github-light", "github-dark"];

const STREAMDOWN_LINK_SAFETY = { enabled: false } as const;

// Component overrides — defined once at module scope.  None of these functions
// close over component state/props, so they are safe to hoist.
const STREAMDOWN_COMPONENTS = {
  // Mermaid code blocks — use our custom branded renderer
  code({ children, className: codeClassName, node, ...props }: any) {
    const isBlock = "data-block" in props;
    const langMatch = /language-mermaid/.exec(codeClassName || "");
    const codeString = String(children).replace(/\n$/, "");

    if (isBlock && langMatch) {
      return <MermaidDiagram code={codeString} />;
    }

    // Inline code
    if (!isBlock) {
      return (
        <code
          className="rounded-sm px-1.5 py-0.5 text-sm"
          style={{
            backgroundColor: "var(--color-prose-code-bg)",
            color: "var(--color-prose-code-text)",
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    // Block code — pass through Streamdown's highlighted content
    return (
      <code
        className={`${codeClassName || ""} block overflow-x-auto rounded-lg p-4 text-sm`}
        style={{
          backgroundColor: "var(--color-prose-pre-bg)",
          borderColor: "var(--color-prose-pre-border)",
          color: "var(--color-prose-pre-text)",
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  // Links — internal API paths get authenticated blob downloads
  a({ children, node, ...props }: any) {
    const href: string | undefined = props.href;

    if (href && isInternalApiPath(href)) {
      // Extract filename from the link text
      const filename =
        typeof children === "string"
          ? children.replace(/^Download\s+/i, "").replace(/\s*\(v\d+\)$/, "")
          : href.split("/").pop() || "download";

      const attachmentUuid = extractAttachmentUuid(href);
      // Previewability must be decided from the REAL filename, not the link
      // text: the LLM often writes prose like "Open PDF preview" or "Download
      // the document" as the link text, which has no usable extension. Prefer
      // the filename registered for this attachment UUID (from message load /
      // streaming); fall back to the link text only when unknown.
      const realFilename = attachmentUuid ? getAttachmentFilename(attachmentUuid) : undefined;
      const previewFilename = realFilename ?? filename;
      const canPreview = attachmentUuid && isPreviewableFile(previewFilename);

      // Look up version info from the registry
      const versionInfo = attachmentUuid ? getAttachmentVersionInfo(attachmentUuid) : undefined;

      const handleDownload = async (e: React.MouseEvent) => {
        e.preventDefault();
        try {
          const baseUrl = getApiBaseUrl();
          const fullUrl = `${baseUrl}${href}`;
          const headers = await getHeaders();
          headers.delete("Content-Type");

          const response = await fetch(fullUrl, { headers });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const blob = await response.blob();
          const url = URL.createObjectURL(blob);

          // Name the download from the REAL filename, not the LLM's prose link
          // text (which produced "file.pdf"). Order of preference:
          //   1. the filename registered for this attachment UUID
          //   2. the server's Content-Disposition header
          //   3. the link text (last resort)
          const downloadName =
            realFilename ?? filenameFromContentDisposition(response.headers.get("Content-Disposition")) ?? filename;

          const a = document.createElement("a");
          a.href = url;
          a.download = downloadName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error("Failed to download file:", err);
        }
      };

      const handlePreview = (e: React.MouseEvent) => {
        e.preventDefault();
        if (attachmentUuid) {
          openDocumentPreview(attachmentUuid, previewFilename);
        }
      };

      const handleHistory = (e: React.MouseEvent) => {
        e.preventDefault();
        if (versionInfo) {
          openDocumentVersionPanel(versionInfo.lineageUuid, versionInfo.sessionUuid);
        }
      };

      return (
        <span className="inline-flex items-center gap-2">
          {canPreview && (
            <button
              type="button"
              className="underline cursor-pointer inline-flex items-center gap-1 bg-transparent border-none p-0 font-[inherit] text-[inherit]"
              style={{ color: "var(--color-prose-link)" }}
              onClick={handlePreview}
            >
              <Eye size={14} />
              {filename}
            </button>
          )}
          {versionInfo && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium"
              style={{
                backgroundColor: "var(--color-surface-tertiary)",
                color: "var(--color-text-tertiary)",
              }}
            >
              v{versionInfo.version}
            </span>
          )}
          <button
            type="button"
            className="underline cursor-pointer inline-flex items-center gap-1 bg-transparent border-none p-0 font-[inherit] text-[inherit]"
            style={{ color: "var(--color-prose-link)" }}
            onClick={handleDownload}
          >
            <Download size={14} />
            {canPreview ? "Download" : children}
          </button>
          {versionInfo && (
            <button
              type="button"
              className="cursor-pointer inline-flex items-center gap-1 bg-transparent border-none p-0 font-[inherit] text-[inherit] opacity-60 hover:opacity-100 transition-opacity"
              style={{ color: "var(--color-prose-link)" }}
              onClick={handleHistory}
              title="Version history"
            >
              <History size={14} />
            </button>
          )}
        </span>
      );
    }

    return (
      <a
        className="underline"
        style={{ color: "var(--color-prose-link)" }}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </a>
    );
  },
  // Headings
  h1({ children, node, ...props }: any) {
    return (
      <h1 className="text-2xl font-bold mt-6 mb-4" style={{ color: "var(--color-text-primary)" }} {...props}>
        {children}
      </h1>
    );
  },
  h2({ children, node, ...props }: any) {
    return (
      <h2 className="text-xl font-bold mt-5 mb-3" style={{ color: "var(--color-text-primary)" }} {...props}>
        {children}
      </h2>
    );
  },
  h3({ children, node, ...props }: any) {
    return (
      <h3 className="text-lg font-semibold mt-4 mb-2" style={{ color: "var(--color-text-primary)" }} {...props}>
        {children}
      </h3>
    );
  },
  // Lists
  ul({ children, node, ...props }: any) {
    return (
      <ul className="list-disc list-inside space-y-1" style={{ color: "var(--color-text-secondary)" }} {...props}>
        {children}
      </ul>
    );
  },
  ol({ children, node, ...props }: any) {
    return (
      <ol className="list-decimal list-inside space-y-1" style={{ color: "var(--color-text-secondary)" }} {...props}>
        {children}
      </ol>
    );
  },
  li({ children, node, ...props }: any) {
    return (
      <li className="[&>p]:inline" {...props}>
        {children}
      </li>
    );
  },
  // Blockquotes
  blockquote({ children, node, ...props }: any) {
    return (
      <blockquote
        className="border-l-4 pl-4 italic"
        style={{
          borderColor: "var(--color-prose-blockquote-border)",
          color: "var(--color-text-tertiary)",
        }}
        {...props}
      >
        {children}
      </blockquote>
    );
  },
  // Tables
  table({ children, node, style, ...props }: any) {
    return (
      <div className="overflow-x-auto my-3">
        <table
          style={{
            ...style,
            borderCollapse: "collapse",
            borderColor: "var(--color-border-secondary)",
            border: "1px solid var(--color-border-secondary)",
          }}
          {...props}
        >
          {children}
        </table>
      </div>
    );
  },
  th({ children, node, style, ...props }: any) {
    return (
      <th
        className="px-4 py-2 text-left font-semibold"
        style={{
          ...style,
          borderWidth: "1px",
          borderColor: "var(--color-border-secondary)",
          backgroundColor: "var(--color-surface-secondary)",
        }}
        {...props}
      >
        {children}
      </th>
    );
  },
  td({ children, node, style, ...props }: any) {
    return (
      <td
        className="px-4 py-2"
        style={{
          ...style,
          borderWidth: "1px",
          borderColor: "var(--color-border-secondary)",
        }}
        {...props}
      >
        {children}
      </td>
    );
  },
  // Paragraphs
  p({ children, node, ...props }: any) {
    return (
      <p className="mb-3" style={{ color: "var(--color-text-secondary)" }} {...props}>
        {children}
      </p>
    );
  },
  // Images — rendered inline with authenticated loading for internal URLs
  img({ node, ...props }: any) {
    return <AuthenticatedImage {...props} />;
  },
  // Pre — wrapper for code blocks
  pre({ children, node, ...props }: any) {
    return (
      <pre
        className="overflow-x-auto rounded-lg text-sm"
        style={{
          backgroundColor: "var(--color-prose-pre-bg)",
          border: "1px solid var(--color-prose-pre-border)",
        }}
        {...props}
      >
        {children}
      </pre>
    );
  },
};

interface MarkdownProps {
  content: string;
  className?: string;
  isStreaming?: boolean;
}

/**
 * Strip the ``sandbox:`` protocol prefix that LLMs sometimes prepend to
 * internal URLs.  Models trained on code-interpreter outputs may emit links
 * like ``sandbox:/chat/attachments/{uuid}/file`` instead of the correct
 * relative path ``/chat/attachments/{uuid}/file``.  The ``sandbox:`` scheme
 * is blocked by Streamdown's ``rehype-harden`` plugin (it only allows
 * ``http:``, ``https:``, and a handful of other safe protocols), which
 * replaces the link with a ``[blocked]`` indicator.
 *
 * This function rewrites both markdown links ``[text](sandbox:/path)`` and
 * markdown images ``![alt](sandbox:/path)`` to use the plain relative path.
 */
export function sanitizeSandboxUrls(text: string): string {
  // Match markdown link/image syntax where the URL starts with sandbox:
  // Handles sandbox:/path and sandbox://path (the LLM may emit either form).
  // We strip "sandbox:" and any leading double-slash, preserving a single
  // leading slash so the result is a valid relative path.
  return text.replace(
    /(!?\[[^\]]*\])\(sandbox:(\/[^)]*)\)/g, // NOSONAR — disjoint char classes, no backtracking risk
    (_match, bracket, rawPath: string) => {
      // Normalise leading // to single /
      const clean = rawPath.startsWith("//") ? rawPath.slice(1) : rawPath;
      return `${bracket}(${clean})`;
    },
  );
}

/**
 * Deduplicate image markdown in content.  When the same image filename
 * appears in multiple `![alt](url)` references (e.g. once with a SAS URL
 * and again with a stable /chat/attachments/ URL), keep only the first
 * occurrence of each filename to avoid displaying the same chart 2-3 times.
 */
export function deduplicateImages(text: string): string {
  // Match markdown image syntax: ![alt](url)
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
  const seenFilenames = new Set<string>();
  return text.replace(imageRegex, (match, alt: string, url: string) => {
    // Extract filename from the alt text or from the URL path
    const filename = alt?.trim() || decodeURIComponent(url.split("/").pop()?.split("?")[0] || "");
    if (!filename) return match;

    // Normalise: strip extension for comparison so "chart.png" and "chart"
    // are treated as the same image.
    const key = filename.replace(/\.\w+$/, "").toLowerCase();
    if (seenFilenames.has(key)) {
      return ""; // duplicate — remove
    }
    seenFilenames.add(key);
    return match;
  });
}

export function Markdown({ content, className = "", isStreaming = false }: Readonly<MarkdownProps>) {
  const resolvedClassName = useMemo(() => `prose max-w-none ${className}`, [className]);

  // 1. Strip sandbox: protocol prefix that LLMs sometimes prepend to URLs
  //    (applied to both streaming and static content).
  // 2. Deduplicate images in persisted content (not during streaming, to
  //    avoid interfering with incremental rendering).
  const processedContent = useMemo(() => {
    const sanitized = sanitizeSandboxUrls(content);
    return isStreaming ? sanitized : deduplicateImages(sanitized);
  }, [content, isStreaming]);

  return (
    <Streamdown
      mode={isStreaming ? "streaming" : "static"}
      plugins={STREAMDOWN_PLUGINS}
      shikiTheme={STREAMDOWN_SHIKI_THEME}
      controls={true}
      linkSafety={STREAMDOWN_LINK_SAFETY}
      className={resolvedClassName}
      components={STREAMDOWN_COMPONENTS}
    >
      {processedContent}
    </Streamdown>
  );
}
